/**
 * Message Bubble Component
 * Display individual message messages (user or AI)
 * Supports multi-goal roadmaps with individual confirmation buttons
 */

import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, Linking, Alert, TouchableOpacity } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import Markdown from 'react-native-markdown-display';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { formatTime } from '../../utils/formatters';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import logger from '../../utils/logger';
import type { Message, GoalProposal } from '../../api/types';
import FinancialGoalCard from './FinancialGoalCard';
import FinancialDiagnosticActionCard from '../financialDiagnostic/FinancialDiagnosticActionCard';
import StreamingMarkdown from './StreamingMarkdown';
import WeeklyCheckCard from '../chat/WeeklyCheckCard';

const RICH_MESSAGE_TYPES: Record<string, React.ComponentType<{ metadata: any }>> = {
  weekly_check: WeeklyCheckCard,
};

interface MessageBubbleProps {
  message: Message;
  isUser: boolean;
  onFinancialGoalConfirm?: (financialGoal: any) => void;
  onConfirmAllGoals?: (goals: GoalProposal[], messageId: number) => void;
  onExecuteArbitrage?: (goalId: string) => void;
  onStartFinancialDiagnosticWizard?: () => void;
  onViewGoal?: (goalId: string) => void;
  onViewProfile?: (section?: string) => void;
  onDiagnosticAction?: (action: 'send_action_plan' | 'ask_question' | 'confirm' | 'correct', messageId?: string) => void;
  onOpenCsvImport?: () => void;
  isConfirmingFinancialGoal?: boolean;
  isConfirmingAllGoals?: boolean;
  isGoalConfirmed?: boolean;
  confirmedGoalId?: string;
  confirmedGoalIds?: string[]; // For multi-goal roadmaps
  isStartingWizard?: boolean;
  isNewMessage?: boolean;
  onStreamingComplete?: () => void;
}

export default function MessageBubble({
  message,
  isUser,
  onFinancialGoalConfirm,
  onConfirmAllGoals,
  onExecuteArbitrage,
  onStartFinancialDiagnosticWizard,
  onViewGoal,
  onViewProfile,
  onDiagnosticAction,
  onOpenCsvImport,
  isConfirmingFinancialGoal = false,
  isConfirmingAllGoals = false,
  isGoalConfirmed = false,
  confirmedGoalId,
  confirmedGoalIds = [],
  isStartingWizard = false,
  isNewMessage = false,
  onStreamingComplete,
}: MessageBubbleProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    await Clipboard.setStringAsync(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [message.content]);

  const bubbleStyle = isUser ? styles.userBubble : styles.aiBubble;
  const textStyle = isUser ? styles.userText : styles.aiText;

  // Extract action button if present (for snapshot wizard trigger)
  const actionButton = !isUser ? message.metadata?.action_button : undefined;
  const isSnapshotAction = actionButton?.type === 'snapshot_wizard';

  // Extract goal proposals (multi-goal roadmap)
  const goalProposals = !isUser ? message.metadata?.goal_proposals : undefined;
  const goalProposal = !isUser ? message.metadata?.goal_proposal : undefined;
  const isMultiGoal = goalProposals && goalProposals.length > 1;
  const isDiagnosticMessage = !isUser && message.metadata?.diagnostic_metadata?.is_diagnostic;

  // Action plan messages (from diagnostic flow)
  const isActionPlanMessage = !isUser && message.metadata?.diagnostic_metadata?.is_action_plan;
  const actionPlanGoals = !isUser ? message.metadata?.goals : undefined;
  const actionButtons = !isUser ? message.metadata?.action_buttons : undefined;
  const isActionPlanMultiGoal = actionPlanGoals && actionPlanGoals.length > 1;
  const isActionPlanSingleGoal = actionPlanGoals && actionPlanGoals.length === 1;

  // Synthesis message buttons (send_action_plan, ask_question)
  const isSynthesisMessage = !isUser && message.metadata?.diagnostic_metadata?.is_synthesis_only;
  const diagnosticActionButtons = !isUser ? (
    actionButtons?.filter((btn: any) => btn.type === 'diagnostic_action')
  ) : undefined;

  // Import nudge buttons (open_csv_import)
  const isImportNudge = !isUser && message.metadata?.import_nudge;
  const importActionButtons = !isUser ? (
    actionButtons?.filter((btn: any) => btn.type === 'import_action')
  ) : undefined;

  // Check if all goals in a multi-goal message are confirmed
  const allGoalsConfirmed = isMultiGoal && confirmedGoalIds.length >= goalProposals.length;

  // Legacy: Extract financial goal data if present in AI message
  const financialGoalData = !isUser ? message.metadata?.ai_metadata?.financial_goal_data : undefined;
  const hasFinancialGoalPending = financialGoalData?.financial_goal_confirmed_pending;
  const pendingFinancialGoal = hasFinancialGoalPending ? financialGoalData.financial_goal : null;

  // Handle link clicks - navigate to goals, profile, or open in external browser
  const handleLinkPress = async (url: string) => {
    try {
      // Check if this is an internal goal link (/objectifs/{id})
      const goalLinkMatch = url.match(/\/objectifs\/([a-zA-Z0-9-]+)/);
      if (goalLinkMatch && onViewGoal) {
        const goalId = goalLinkMatch[1];
        logger.log('[MessageBubble] Navigating to goal:', goalId);
        onViewGoal(goalId);
        return;
      }

      // Check if this is an internal profile link (/profile#section)
      const profileLinkMatch = url.match(/\/profile(?:#([a-zA-Z0-9-]+))?/);
      if (profileLinkMatch && onViewProfile) {
        const section = profileLinkMatch[1]; // e.g., 'financial'
        logger.log('[MessageBubble] Navigating to profile, section:', section);
        onViewProfile(section);
        return;
      }

      // External link - open in browser
      const supported = await Linking.canOpenURL(url);
      if (supported) {
        await Linking.openURL(url);
      } else {
        Alert.alert('Erreur', `Impossible d'ouvrir le lien: ${url}`);
      }
    } catch (error) {
      logger.error('Error opening link:', error);
      Alert.alert('Erreur', 'Impossible d\'ouvrir le lien');
    }
  };

  // Custom markdown styles for links and diagnostic sections
  const markdownStyles = {
    body: textStyle,
    paragraph: { marginBottom: spacing.xs },
    link: {
      color: colors.primary,
      textDecorationLine: 'underline' as const,
    },
    // Style for bullet lists
    bullet_list: {
      marginVertical: spacing.xs,
    },
    ordered_list: {
      marginVertical: spacing.xs,
    },
    list_item: {
      marginVertical: spacing.xs / 2,
    },
    // Bold text for section headers (SYNTHÈSE, PLAN D'ACTION)
    strong: {
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
    },
    // Horizontal rule separator between sections
    hr: {
      backgroundColor: colors.border,
      height: 1,
      marginVertical: spacing.md,
    },
    // Headings if any
    heading1: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      marginVertical: spacing.sm,
      color: colors.text,
    },
    heading2: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      marginVertical: spacing.sm,
      color: colors.text,
    },
    // Blockquote for educational context
    blockquote: {
      backgroundColor: colors.primary + '10',
      borderLeftWidth: 3,
      borderLeftColor: colors.primary,
      paddingLeft: spacing.sm,
      paddingVertical: spacing.xs,
      marginVertical: spacing.xs,
    },
  };

  // Rich message component lookup
  const RichComponent = !isUser && message.metadata?.message_type
    ? RICH_MESSAGE_TYPES[message.metadata.message_type]
    : null;

  return (
    <View style={[styles.container, isUser && styles.userContainer]}>
      <View style={[styles.bubble, bubbleStyle]}>
        {RichComponent ? (
          <RichComponent metadata={message.metadata} />
        ) : isUser ? (
          <Text style={textStyle}>{message.content}</Text>
        ) : isNewMessage ? (
          <StreamingMarkdown
            content={message.content}
            markdownStyles={markdownStyles}
            onLinkPress={handleLinkPress}
            onStreamingComplete={onStreamingComplete}
          />
        ) : (
          <Markdown
            style={markdownStyles}
            onLinkPress={handleLinkPress}
          >
            {message.content}
          </Markdown>
        )}

        {/* Render Financial Diagnostic Action Card if action button present */}
        {isSnapshotAction && actionButton && onStartFinancialDiagnosticWizard && (
          <FinancialDiagnosticActionCard
            actionButton={actionButton}
            onStartWizard={onStartFinancialDiagnosticWizard}
            isLoading={isStartingWizard}
          />
        )}

        {/* Synthesis message: Render diagnostic action buttons (send_action_plan, ask_question) */}
        {isSynthesisMessage && diagnosticActionButtons && diagnosticActionButtons.length > 0 && onDiagnosticAction && (
          <View style={styles.diagnosticActionsContainer}>
            {diagnosticActionButtons.map((btn: any, index: number) => (
              <TouchableOpacity
                key={`diag-action-${index}`}
                style={[
                  styles.diagnosticActionButton,
                  btn.style === 'success' && styles.diagnosticActionButtonPrimary,
                  btn.style === 'outline' && styles.diagnosticActionButtonOutline,
                ]}
                onPress={() => onDiagnosticAction(btn.action, message.id)}
              >
                <Text style={[
                  styles.diagnosticActionButtonText,
                  btn.style === 'success' && styles.diagnosticActionButtonTextPrimary,
                  btn.style === 'outline' && styles.diagnosticActionButtonTextOutline,
                ]}>
                  {btn.label}
                </Text>
                {btn.description && (
                  <Text style={[
                    styles.diagnosticActionButtonDesc,
                    btn.style === 'success' && styles.diagnosticActionButtonDescPrimary,
                  ]}>{btn.description}</Text>
                )}
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Import nudge: Render import action buttons (open_csv_import) */}
        {isImportNudge && importActionButtons && importActionButtons.length > 0 && onOpenCsvImport && (
          <View style={styles.diagnosticActionsContainer}>
            {importActionButtons.map((btn: any, index: number) => (
              <TouchableOpacity
                key={`import-action-${index}`}
                style={[styles.diagnosticActionButton, styles.importActionButton]}
                onPress={() => onOpenCsvImport()}
              >
                <Text style={[styles.diagnosticActionButtonText, styles.importActionButtonText]}>
                  {btn.label}
                </Text>
                {btn.description && (
                  <Text style={styles.importActionButtonDesc}>{btn.description}</Text>
                )}
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Multi-Goal Roadmap: Render "Confirm All" button + individual goal cards */}
        {isDiagnosticMessage && isMultiGoal && goalProposals && onFinancialGoalConfirm && !allGoalsConfirmed && !isGoalConfirmed && (
          <View style={styles.roadmapContainer}>
            {/* Confirm All Button */}
            {onConfirmAllGoals && confirmedGoalIds.length === 0 && !isGoalConfirmed && (
              <TouchableOpacity
                style={[styles.confirmAllButton, isConfirmingAllGoals && styles.buttonDisabled]}
                onPress={() => onConfirmAllGoals(goalProposals, message.id)}
                disabled={isConfirmingAllGoals}
              >
                <Text style={styles.confirmAllButtonText}>
                  {isConfirmingAllGoals ? 'Ajout en cours...' : '✅ Ajouter tous les objectifs'}
                </Text>
              </TouchableOpacity>
            )}

            {/* Individual Goal Cards */}
            {goalProposals.map((goal, index) => {
              const isThisGoalConfirmed = confirmedGoalIds.length > index;
              const thisConfirmedGoalId = confirmedGoalIds[index];

              return (
                <FinancialGoalCard
                  key={`goal-${index}-${goal.title}`}
                  financialGoal={goal}
                  goalIndex={index}
                  onConfirm={() => onFinancialGoalConfirm({ ...goal, messageId: message.id, goalIndex: index })}
                  onExecuteArbitrage={onExecuteArbitrage}
                  onViewGoal={onViewGoal}
                  isLoading={isConfirmingFinancialGoal}
                  isConfirmed={isThisGoalConfirmed}
                  confirmedGoalId={thisConfirmedGoalId}
                />
              );
            })}
          </View>
        )}

        {/* Single Goal: Render single Diagnostic Goal Proposal */}
        {isDiagnosticMessage && !isMultiGoal && goalProposal && onFinancialGoalConfirm && !isGoalConfirmed && (
          <FinancialGoalCard
            financialGoal={goalProposal}
            onConfirm={() => onFinancialGoalConfirm({ ...goalProposal, messageId: message.id })}
            onExecuteArbitrage={onExecuteArbitrage}
            isLoading={isConfirmingFinancialGoal}
          />
        )}

        {/* Legacy: Render Financial Goal Card if pending confirmation and not yet confirmed */}
        {!isDiagnosticMessage && pendingFinancialGoal && onFinancialGoalConfirm && !isGoalConfirmed && (
          <FinancialGoalCard
            financialGoal={pendingFinancialGoal as any}
            onConfirm={() => onFinancialGoalConfirm({ ...pendingFinancialGoal, messageId: message.id })}
            isLoading={isConfirmingFinancialGoal}
          />
        )}

        {/* Show confirmed state with navigation to goal (single goal) */}
        {!isMultiGoal && (pendingFinancialGoal || goalProposal) && isGoalConfirmed && (
          <FinancialGoalCard
            financialGoal={(goalProposal || pendingFinancialGoal) as any}
            onConfirm={() => {}}
            onExecuteArbitrage={onExecuteArbitrage}
            isConfirmed={true}
            confirmedGoalId={confirmedGoalId}
            onViewGoal={onViewGoal}
          />
        )}

        {/* Multi-goal: Show all confirmed goals */}
        {isMultiGoal && allGoalsConfirmed && goalProposals && (
          <View style={styles.roadmapContainer}>
            <View style={styles.allConfirmedBadge}>
              <Text style={styles.allConfirmedText}>🎉 Tous les objectifs ajoutés !</Text>
            </View>
            {goalProposals.map((goal, index) => (
              <FinancialGoalCard
                key={`confirmed-goal-${index}`}
                financialGoal={goal}
                goalIndex={index}
                onConfirm={() => {}}
                onExecuteArbitrage={onExecuteArbitrage}
                onViewGoal={onViewGoal}
                isConfirmed={true}
                confirmedGoalId={confirmedGoalIds[index]}
              />
            ))}
          </View>
        )}

        {/* Action Plan: Single goal with "Ajouter à mes objectifs" button */}
        {isActionPlanMessage && isActionPlanSingleGoal && actionPlanGoals && onFinancialGoalConfirm && !isGoalConfirmed && (
          <View style={styles.actionPlanContainer}>
            <TouchableOpacity
              style={[styles.addGoalButton, isConfirmingFinancialGoal && styles.buttonDisabled]}
              onPress={() => onFinancialGoalConfirm({ ...actionPlanGoals[0], messageId: message.id })}
              disabled={isConfirmingFinancialGoal}
            >
              <Text style={styles.addGoalButtonText}>
                {isConfirmingFinancialGoal ? 'Ajout en cours...' : '+ Ajouter à mes objectifs'}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Action Plan: Multiple goals with "Ajouter tous les objectifs" button */}
        {isActionPlanMessage && isActionPlanMultiGoal && actionPlanGoals && onConfirmAllGoals && !allGoalsConfirmed && confirmedGoalIds.length === 0 && !isGoalConfirmed && (
          <View style={styles.actionPlanContainer}>
            <TouchableOpacity
              style={[styles.confirmAllButton, isConfirmingAllGoals && styles.buttonDisabled]}
              onPress={() => onConfirmAllGoals(actionPlanGoals, message.id)}
              disabled={isConfirmingAllGoals}
            >
              <Text style={styles.confirmAllButtonText}>
                {isConfirmingAllGoals ? 'Ajout en cours...' : '✅ Ajouter tous les objectifs'}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Action Plan: Show confirmed state */}
        {isActionPlanMessage && actionPlanGoals && isGoalConfirmed && (
          <View style={styles.actionPlanContainer}>
            <View style={styles.goalAddedBadge}>
              <Text style={styles.goalAddedText}>✓ Objectif ajouté à ta liste</Text>
              {confirmedGoalId && onViewGoal && (
                <TouchableOpacity onPress={() => onViewGoal(confirmedGoalId)}>
                  <Text style={styles.viewGoalLink}>Voir l'objectif →</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}

        <View style={styles.footerRow}>
          <Text style={[styles.timestamp, textStyle]}>
            {formatTime(message.created_at)}
          </Text>
          <TouchableOpacity onPress={handleCopy} style={styles.copyButton} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Icon
              name={copied ? 'check' : 'content-copy'}
              size={14}
              color={copied ? colors.success : (isUser ? colors.userMessageText : colors.textSecondary)}
            />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginVertical: spacing.xs,
    paddingHorizontal: spacing.md,
    alignItems: 'flex-start',
  },
  userContainer: {
    alignItems: 'flex-end',
  },
  bubble: {
    maxWidth: '80%',
    padding: spacing.md,
    borderRadius: 16,
  },
  userBubble: {
    backgroundColor: colors.userMessage,
    borderBottomRightRadius: 4,
  },
  aiBubble: {
    backgroundColor: colors.aiMessage,
    borderBottomLeftRadius: 4,
  },
  userText: {
    fontSize: typography.fontSize.md,
    color: colors.userMessageText,
    lineHeight: typography.lineHeight.normal * typography.fontSize.md,
  },
  aiText: {
    fontSize: typography.fontSize.md,
    color: colors.aiMessageText,
    lineHeight: typography.lineHeight.normal * typography.fontSize.md,
  },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.xs,
  },
  timestamp: {
    fontSize: typography.fontSize.xs,
    opacity: 0.7,
  },
  copyButton: {
    marginLeft: spacing.sm,
    opacity: 0.6,
  },
  confirmedBadge: {
    backgroundColor: '#E8F5E9',
    borderRadius: 8,
    padding: spacing.sm,
    marginTop: spacing.sm,
    borderLeftWidth: 3,
    borderLeftColor: '#2E7D32',
  },
  confirmedText: {
    fontSize: typography.fontSize.sm,
    color: '#2E7D32',
    fontWeight: typography.fontWeight.semibold,
  },
  roadmapContainer: {
    marginTop: spacing.md,
  },
  confirmAllButton: {
    backgroundColor: colors.success,
    borderRadius: 10,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    alignItems: 'center',
    marginBottom: spacing.md,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  confirmAllButtonText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  allConfirmedBadge: {
    backgroundColor: colors.success + '15',
    borderRadius: 10,
    padding: spacing.md,
    alignItems: 'center',
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.success,
  },
  allConfirmedText: {
    color: colors.success,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
  },
  actionPlanContainer: {
    marginTop: spacing.md,
  },
  addGoalButton: {
    backgroundColor: colors.primary,
    borderRadius: 10,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  addGoalButtonText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
  },
  goalAddedBadge: {
    backgroundColor: colors.success + '15',
    borderRadius: 10,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.success,
  },
  goalAddedText: {
    color: colors.success,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
  },
  viewGoalLink: {
    color: colors.primary,
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs,
    textDecorationLine: 'underline',
  },
  // Diagnostic action buttons (synthesis flow)
  diagnosticActionsContainer: {
    marginTop: spacing.md,
    gap: spacing.sm,
  },
  diagnosticActionButton: {
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: 10,
    backgroundColor: colors.backgroundSecondary,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
  },
  diagnosticActionButtonPrimary: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  diagnosticActionButtonOutline: {
    backgroundColor: 'transparent',
    borderColor: colors.textSecondary,
  },
  diagnosticActionButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  diagnosticActionButtonTextPrimary: {
    color: '#FFFFFF',
  },
  diagnosticActionButtonTextOutline: {
    color: colors.textSecondary,
  },
  diagnosticActionButtonDesc: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  diagnosticActionButtonDescPrimary: {
    color: 'rgba(255, 255, 255, 0.85)',
  },
  importActionButton: {
    backgroundColor: colors.primary + '12',
    borderColor: colors.primary,
    borderWidth: 1.5,
  },
  importActionButtonText: {
    color: colors.primary,
    fontWeight: typography.fontWeight.bold,
  },
  importActionButtonDesc: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
});

